import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import profileApi from '@/apis/user'
import { uploadFileToStorage } from '@/apis/storage'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/store/useAuthStore'
import { post } from '@/utils/https'
import { fetchAndHydrateAuthSession } from '@/lib/auth/hydrateFromAuthSession'
import { queryClient } from '@/lib/react-query'
import { getCurrentUserQueryKey } from '@/hooks/queries/useCurrentUser'
import { invalidateProfileAvatarQueries } from '@/hooks/queries/invalidateAvatarCaches'
import { useApiReady } from '@/hooks/useApiReady'
import { jobTitlesKeys } from '@/hooks/queries/useJobTitles'

// Query keys
export const profileKeys = {
  all: ['profile'] as const,
  freelancer: () => [...profileKeys.all, 'freelancer'] as const,
}

/**
 * Hook pour récupérer le profil freelancer
 */
export function useFreelancerProfile(enabled = true) {
  const apiReady = useApiReady()

  return useQuery({
    queryKey: profileKeys.freelancer(),
    queryFn: async () => {
      const res = await profileApi.freelancersDetail()
      // get() retourne directement res.data, donc res contient { code, data, message }
      if (res.code === 200) {
        return res.data
      }
      throw new Error(res.message || 'Failed to fetch freelancer profile')
    },
    enabled: apiReady && enabled,
    retry: 1,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
  })
}

/**
 * Hook pour mettre à jour le profil utilisateur
 */
export function useUpdateProfile() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { setProfile } = useAuthStore()
  
  return useMutation({
    mutationFn: async (params: {
      first_name?: string
      last_name?: string
      email?: string
      job_title?: string
      avatar?: string | null
    }) => {
      try {
        const res = await profileApi.updateProfile(params)
        // patch() retourne res.data, donc res contient { code, data, message }
        if (res.code === 200) {
          return res.data
        }
        console.error('[useUpdateProfile] Error: code is not 200:', res.code, res.message)
        throw new Error(
          res.message === 'Forbidden: email change is not allowed during impersonation'
            ? t('profile.email_change_disabled_impersonation')
            : res.message || 'Failed to update profile'
        )
      } catch (error) {
        console.error('[useUpdateProfile] Exception caught:', error)
        throw error
      }
    },
    onSuccess: async (data, variables) => {
      const { user } = useAuthStore.getState()

      // Mettre à jour le store utilisateur avec les données de l'API
      if (user && data && typeof data === 'object' && 'user' in data && data.user) {
        setProfile({
          ...user,
          ...data.user,
        })
      } else if (user) {
        setProfile({
          ...user,
          ...variables,
        })
      }
      
      // Si l'email a été mis à jour, rafraîchir la session Supabase pour obtenir les nouvelles données
      if (variables.email) {
        try {
          await fetchAndHydrateAuthSession()
        } catch (error) {
          console.error('[useUpdateProfile] Error refreshing session:', error)
        }
      }
      
      toast.success(t('profile.update_success'))
      
      // Invalider les queries liées au profil
      queryClient.invalidateQueries({ queryKey: profileKeys.all })
      
      if (variables.avatar !== undefined) {
        invalidateProfileAvatarQueries(queryClient)
      }
      
      // Invalider les job titles si le job_title a été modifié (pour les team members)
      if (variables.job_title !== undefined) {
        queryClient.invalidateQueries({ queryKey: jobTitlesKeys.all })
      }
    },
    onError: (error: Error) => {
      console.error('Failed to update profile:', error)
      toast.error(t('profile.update_error'))
    },
  })
}

const AVATAR_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const AVATAR_MAX_BYTES = 10 * 1024 * 1024

/**
 * Hook pour uploader et persister la photo de profil (upload immédiat).
 */
export function useUpdateAvatar() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { setProfile } = useAuthStore()

  return useMutation({
    mutationFn: async (file: File) => {
      const { user } = useAuthStore.getState()
      if (!user?.id) {
        throw new Error('User not authenticated')
      }

      const mimeType = file.type.split(';')[0].trim()
      if (!AVATAR_ACCEPTED_TYPES.includes(mimeType)) {
        throw new Error(t('profile.avatar.invalidType'))
      }

      if (file.size > AVATAR_MAX_BYTES) {
        throw new Error(t('profile.avatar.fileTooLarge'))
      }

      const uploadRes = await uploadFileToStorage(file, {
        module: 'avatars',
        module_id: user.id,
      })

      type UploadApiResponse = {
        code: number
        data?: { fileKey?: string; fileURL?: string }
        message?: string
      }

      const uploadResponse = uploadRes as UploadApiResponse
      if (uploadResponse.code !== 200 || !uploadResponse.data?.fileKey) {
        throw new Error(uploadResponse.message || t('profile.avatar.uploadError'))
      }

      const fileKey = uploadResponse.data.fileKey

      let profileRes: { code: number; data?: unknown; message?: string }
      try {
        profileRes = await profileApi.updateProfile({ avatar: fileKey })
      } catch (err) {
        const apiMessage =
          (err as { data?: { message?: string } })?.data?.message ??
          (err as { message?: string })?.message
        throw new Error(apiMessage || t('profile.avatar.uploadError'))
      }

      if (profileRes.code !== 200) {
        throw new Error(profileRes.message || t('profile.avatar.uploadError'))
      }

      return { fileKey, profileData: profileRes.data }
    },
    onSuccess: async (data) => {
      const { user } = useAuthStore.getState()
      if (user) {
        const updatedUser =
          data.profileData &&
          typeof data.profileData === 'object' &&
          'user' in data.profileData &&
          data.profileData.user
            ? { ...user, ...data.profileData.user }
            : { ...user, avatar: data.fileKey }
        setProfile(updatedUser)
      }

      toast.success(t('profile.avatar.uploadSuccess'))

      invalidateProfileAvatarQueries(queryClient)
    },
    onError: (error: Error) => {
      console.error('Failed to update avatar:', error)
      toast.error(error.message || t('profile.avatar.uploadError'))
    },
  })
}

/**
 * Hook pour supprimer la photo de profil (revient aux initiales).
 */
export function useRemoveAvatar() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { setProfile } = useAuthStore()

  return useMutation({
    mutationFn: async () => {
      const { user } = useAuthStore.getState()
      if (!user?.id) {
        throw new Error('User not authenticated')
      }

      const profileRes = await profileApi.updateProfile({ avatar: null })
      if (profileRes.code !== 200) {
        throw new Error(profileRes.message || t('profile.avatar.removeError'))
      }

      return profileRes.data
    },
    onSuccess: async (data) => {
      const { user } = useAuthStore.getState()
      if (user) {
        const updatedUser =
          data &&
          typeof data === 'object' &&
          'user' in data &&
          data.user
            ? { ...user, ...data.user }
            : { ...user, avatar: null }
        setProfile(updatedUser)
      }

      toast.success(t('profile.avatar.removeSuccess'))
      invalidateProfileAvatarQueries(queryClient)
    },
    onError: (error: Error) => {
      console.error('Failed to remove avatar:', error)
      toast.error(error.message || t('profile.avatar.removeError'))
    },
  })
}

function getChangePasswordErrorMessage(
  apiMessage: string | undefined,
  t: (key: string) => string
): string {
  switch (apiMessage) {
    case 'Current password is incorrect':
      return t('profile.current_password_incorrect')
    case 'Current password is required':
      return t('profile.current_password_required')
    case 'New password and confirmation do not match':
      return t('profile.confirm_new_password_failed')
    case 'New password must be different from current password':
      return t('profile.new_password_same_as_current')
    case 'Forbidden: password change is not allowed during impersonation':
      return t('profile.password_change_disabled_impersonation')
    default:
      return apiMessage || t('profile.password_change_error')
  }
}

function applyAuthIdentityToClient(identityInfo: {
  auth_providers: string[]
  has_password: boolean
}) {
  const { user, activeTeamId } = useAuthStore.getState()

  if (user) {
    useAuthStore.setState({
      user: {
        ...user,
        auth_providers: identityInfo.auth_providers,
        has_password: identityInfo.has_password,
      },
    })

    const queryKey = getCurrentUserQueryKey(user.id, activeTeamId)
    const cached = queryClient.getQueryData<{ user: Record<string, unknown> }>(queryKey)
    if (cached?.user) {
      queryClient.setQueryData(queryKey, {
        ...cached,
        user: {
          ...cached.user,
          auth_providers: identityInfo.auth_providers,
          has_password: identityInfo.has_password,
        },
      })
    }
  }
}

/**
 * Hook pour changer ou définir le mot de passe via l'API backend (cookies HttpOnly).
 */
export function useChangePassword() {
  const { t } = useTranslation()
  
  return useMutation({
    mutationFn: async (params: {
      current_password?: string
      new_password: string
      confirm_new_password: string
      isSettingPassword?: boolean
    }) => {
      if (params.new_password !== params.confirm_new_password) {
        throw new Error(t('profile.confirm_new_password_failed'))
      }

      const passwordRegex = /(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9]).{8,}/
      if (!passwordRegex.test(params.new_password)) {
        throw new Error(t('login.password_requirements'))
      }

      const payload: Record<string, string> = {
        new_password: params.new_password,
        confirm_new_password: params.confirm_new_password,
      }
      if (params.current_password) {
        payload.current_password = params.current_password
      }

      try {
        const response = await post('/api/app/v1/auth/change-password', payload, {
          hideMessageModal: true,
        })
        if (response.code !== 200) {
          throw new Error(getChangePasswordErrorMessage(response.message, t))
        }

        return {
          success: true,
          isSettingPassword: params.isSettingPassword === true,
          identityInfo: response.data as
            | { auth_providers: string[]; has_password: boolean }
            | undefined,
        }
      } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        const apiMessage = axiosError.response?.data?.message

        if (apiMessage) {
          throw new Error(getChangePasswordErrorMessage(apiMessage, t))
        }

        if (error instanceof Error) {
          throw error
        }

        throw new Error(t('profile.password_change_error'))
      }
    },
    onSuccess: async (result) => {
      toast.success(
        result.isSettingPassword
          ? t('profile.password_set_success')
          : t('profile.password_change_success')
      )

      if (result.identityInfo) {
        applyAuthIdentityToClient(result.identityInfo)
      }

      await fetchAndHydrateAuthSession()
    },
    onError: (error: Error) => {
      console.error('Failed to change password:', error)
      toast.error(error.message || t('profile.password_change_error'))
    },
  })
}

function extractFreelancerProfileEditApiMessage(error: unknown): string | undefined {
  if (error && typeof error === 'object') {
    const axiosLike = error as { data?: { message?: string } }
    if (typeof axiosLike.data?.message === 'string') {
      return axiosLike.data.message
    }
  }
  if (error instanceof Error) {
    return error.message
  }
  return undefined
}

function getFreelancerProfileEditErrorMessage(
  apiMessage: string | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  switch (apiMessage) {
    case 'VAT number is required for French company and auto-entrepreneur':
      return t('freelancerOnboarding.validation.missingFields', {
        fields: t('freelancerSettings.company.vatNumber'),
      })
    case 'Invalid French VAT number format (expected FR followed by 11 digits)':
      return t('freelancerOnboarding.validation.invalidVAT')
    case 'VAT number does not match the provided SIREN':
      return t('freelancerSettings.company.vatNumberSirenMismatch')
    case 'Forbidden: email change is not allowed during impersonation':
      return t('profile.email_change_disabled_impersonation')
    default:
      return apiMessage || t('freelancerSettings.updateError')
  }
}

/**
 * Hook pour mettre à jour le profil freelancer
 */
export function useUpdateFreelancerProfile() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { setProfile } = useAuthStore()
  
  return useMutation({
    mutationFn: async (params: {
      first_name?: string
      last_name?: string
      email?: string
      phone_number?: string
      linkedin_url?: string
      keyboard_layout?: string | null
      emergency_contact_name?: string
      emergency_contact_phone?: string
      company_type?: string
      company_name?: string
      company_title?: string
      address?: string
      postal_code?: string
      city?: string
      country?: string
      registre_location?: string
      siren_number?: string
      vat_number?: string | null
      capital?: number | string
      birth_date?: string
      birth_city?: string
      has_cir_accreditation?: boolean
      cir_accreditation_number?: string
      iban?: string
      payment_zone?: 'sepa' | 'international'
      bank_country?: string
      account_number?: string
      routing_number?: string
      sort_code?: string
      transit_number?: string
      institution_number?: string
      bsb_code?: string
      account_type?: 'checking' | 'savings'
      swift_bic?: string
      bank_name?: string
      account_currency?: string
    }) => {
      try {
        const { freelancerProfileEdit } = await import('@/apis/freelancers')
        const res = await freelancerProfileEdit(params)
        // service.put() retourne l'objet axios ; le corps API est dans res.data
        type ApiResponse = { code: number; data: unknown; message?: string }
        type AxiosResponse = { data: ApiResponse }
        const responseData: ApiResponse = (res as AxiosResponse)?.data || (res as ApiResponse)
        if (responseData?.code === 200) {
          return responseData.data
        }
        throw new Error(
          getFreelancerProfileEditErrorMessage(responseData?.message, t),
        )
      } catch (error) {
        const apiMessage = extractFreelancerProfileEditApiMessage(error)
        throw new Error(getFreelancerProfileEditErrorMessage(apiMessage, t))
      }
    },
    onSuccess: (_data, variables) => {
      toast.success(t('freelancerSettings.updateSuccess'))
      // Mettre à jour le store utilisateur si nécessaire
      const { user } = useAuthStore.getState()
      if (user && (variables.first_name || variables.last_name || variables.email)) {
        setProfile({
          ...user,
          ...(variables.first_name && { first_name: variables.first_name }),
          ...(variables.last_name && { last_name: variables.last_name }),
          ...(variables.email && { email: variables.email }),
        })
      }
      // Invalider les queries liées au profil freelancer
      queryClient.invalidateQueries({ queryKey: profileKeys.freelancer() })
    },
    onError: (error: Error) => {
      console.error('Failed to update freelancer profile:', error)
      toast.error(error.message || t('freelancerSettings.updateError'))
    },
  })
}

